import {
  inject,
  Injectable,
  makeStateKey,
  Signal,
  signal,
  TransferState,
} from '@angular/core';
import type { MenuItem } from '@openng/optimus-ui/api';
import { BehaviorSubject, Observable } from 'rxjs';

interface HeaderState {
  color: HeaderColor;
  sticky: boolean;
  hidden: boolean;
}

/**
 * The header the server rendered. Without it the browser hydrates the default
 * header, which is not sticky and lets the page slide up under it, until the
 * page sets its type again in `ngOnInit`, after its route resolvers finish.
 */
const HEADER_STATE = makeStateKey<HeaderState>('padspin-header-state');

@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  private readonly transferState = inject(TransferState);
  private readonly serverState = this.transferState.get(HEADER_STATE, null);
  private readonly defaultColor: HeaderColor = 'transparent';
  private readonly defaultOptions: HeaderOptions = {
    type: 'filled',
    background: 'primary',
  };
  private readonly color = new BehaviorSubject<HeaderColor>(
    this.serverState?.color ?? this.defaultColor,
  );
  private readonly sticky = new BehaviorSubject(
    this.serverState?.sticky ?? false,
  );
  private readonly isHidden = signal(this.serverState?.hidden ?? false);
  /** Per-page header menu items. Empty by default so pages opt in. */
  private readonly menuItems = signal<MenuItem[]>([]);
  /**
   * Per-page "cross-persona" link rendered next to "Post Your Pad" on the
   * marketing header (e.g. "I'm a Renter" on the landlord page,
   * "I'm a Landlord" on the renter page). Null = nothing rendered.
   */
  private readonly altLandingLink = signal<AltLandingLink | null>(null);

  setType(
    typeOrOptions: HeaderType | HeaderOptions = this.defaultOptions,
  ): void {
    this.applyType(typeOrOptions);
    this.transferState.set(HEADER_STATE, {
      color: this.color.value,
      sticky: this.sticky.value,
      hidden: this.isHidden(),
    });
  }

  private applyType(typeOrOptions: HeaderType | HeaderOptions): void {
    if (typeof typeOrOptions === 'string') {
      this.isHidden.set(typeOrOptions === 'hidden');
      const type: HeaderType = typeOrOptions;
      this.color.next(type === 'transparent' ? 'transparent' : 'white');
      this.sticky.next(type === 'filled');
      return;
    }
    const options: HeaderOptions = typeOrOptions;
    switch (options.type) {
      case 'transparent':
        this.color.next('transparent');
        this.sticky.next(false);
        this.isHidden.set(false);
        return;
      case 'filled':
        this.color.next(options.background);
        this.sticky.next(options.type === 'filled');
        this.isHidden.set(false);
        return;
      case 'hidden':
        this.isHidden.set(true);
        return;
    }
  }

  getBackground$(): Observable<HeaderColor> {
    return this.color.asObservable();
  }

  getSticky$(): Observable<boolean> {
    return this.sticky.asObservable();
  }

  getHidden(): Signal<boolean> {
    return this.isHidden.asReadonly();
  }

  /**
   * Publish the page-specific header menu items. Call in a page's `ngOnInit`
   * (like `setType`). Pass `[]` (or omit) to clear — the default is no items.
   * SSR-safe: a plain signal write.
   */
  setMenuItems(items: MenuItem[] = []): void {
    this.menuItems.set(items);
  }

  getMenuItems(): Signal<MenuItem[]> {
    return this.menuItems.asReadonly();
  }

  /**
   * Publish (or clear) the cross-persona link rendered alongside
   * "Post Your Pad". Call in `ngOnInit` like `setMenuItems`. Pass `null` (or
   * omit) to hide it. SSR-safe: a plain signal write.
   */
  setAltLandingLink(link: AltLandingLink | null = null): void {
    this.altLandingLink.set(link);
  }

  getAltLandingLink(): Signal<AltLandingLink | null> {
    return this.altLandingLink.asReadonly();
  }
}

export type HeaderColor = 'transparent' | 'white' | 'primary';
export type HeaderType = 'transparent' | 'filled' | 'hidden';

/** Persona-switch link in the header (e.g. "I'm a Renter" → /rent). */
export interface AltLandingLink {
  label: string;
  routerLink: string;
}

export type HeaderOptions =
  | {
      type: 'transparent';
    }
  | { type: 'filled'; background: HeaderColor }
  | { type: 'hidden' };
